import type { SelectedFilterOption } from 'features/charts-v2/types'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

/** Ask Yogi's spelling of what the score filter's `valueType` says. */
export const RATING_ONLY_FIELD = 'is_rating_only'

const REVIEWS_ONLY = 1
const RATINGS_ONLY = 2

const isNull = (value: string): boolean =>
  value === 'Null' || value === ALIAS_FOR_NULL_VALUES

const isScoreFilter = (f: SelectedFilterOption): boolean =>
  f.field === 'score' || f.field === 'post_score'

/** The `valueType` a stored `is_rating_only` filter means, or null when the
 *  score filter cannot say the same thing (`is not`, "No Value", both flags). */
function ratingOnlyValueType(f: SelectedFilterOption): number | null {
  if (f.operator !== 'is' || f.values.length !== 1) return null
  const flag = f.values[0]!.toLowerCase()
  if (flag === 'false') return REVIEWS_ONLY
  if (flag === 'true') return RATINGS_ONLY
  return null
}

/**
 * Fold every stored `is_rating_only` filter into the score filter's
 * `valueType`, the one encoding the rating-type control reads and writes, so a
 * hash-loaded "reviews only" chart can't show "Reviews & Ratings".
 *
 * A pure re-encoding: the folded panel must emit exactly the stored query. So
 * `values` is never touched (no "No Value" bucket for reviews-only, unlike the
 * control's `getRatingValues`), and a score filter holding "No Value" is never
 * merged into, because the score filter ORs that bucket with its `valueType`.
 * Anything the score filter can't express exactly is left as stored.
 */
export function foldRatingOnlyFilter(
  filters: SelectedFilterOption[],
): SelectedFilterOption[] {
  let folded = filters
  for (const ratingOnly of filters) {
    if (ratingOnly.field !== RATING_ONLY_FIELD) continue
    const valueType = ratingOnlyValueType(ratingOnly)
    if (valueType === null) continue

    const score = folded.find(isScoreFilter)
    if (score && score.operator !== 'is') continue
    if (score?.values.some(isNull)) continue
    if (score?.valueType && score.valueType !== valueType) continue

    // `post_score` and `score` emit the same filter; `score` is what the
    // control matches on.
    const merged: SelectedFilterOption = {
      ...(score ?? { operator: 'is', values: [] }),
      field: 'score',
      valueType,
    }
    folded = score
      ? folded.map((f) => (f === score ? merged : f))
      : [...folded, merged]
    folded = folded.filter((f) => f !== ratingOnly)
  }
  return folded
}

/**
 * The inverse of `foldRatingOnlyFilter`, for translators whose score shape
 * gates on min/max (`convertFilterOptionsToCriteria`, `convertFilterOption`):
 * a `valueType` with no range splits back into its stars and the exact
 * `is_rating_only` filter. The control's star checkboxes also write this shape,
 * so it is split only where AND matches the query: "No Value" alone is ORed
 * with the `valueType` (see `ratingFilter`), so that shape is left whole.
 */
export function unfoldRatingOnlyFilter(
  filters: SelectedFilterOption[],
): SelectedFilterOption[] {
  return filters.flatMap((f) => {
    if (
      !isScoreFilter(f) ||
      f.operator !== 'is' ||
      f.min !== undefined ||
      f.max !== undefined ||
      (f.valueType !== REVIEWS_ONLY && f.valueType !== RATINGS_ONLY) ||
      (f.values.some(isNull) && f.values.every(isNull))
    ) {
      return [f]
    }
    const { valueType, ...stars } = f
    const ratingOnly: SelectedFilterOption = {
      field: RATING_ONLY_FIELD,
      operator: 'is',
      values: [valueType === REVIEWS_ONLY ? 'False' : 'True'],
    }
    return stars.values.length > 0 ? [stars, ratingOnly] : [ratingOnly]
  })
}
